/** Aligns with server default `ACTIVITY_LOG_POST_RATE_WINDOW_MS` (5 minutes). */
const DEFAULT_COOLDOWN_MS = 5 * 60 * 1000;

let blockedUntil: number | null = null;

function cooldownMs(waitTimeMinutes?: number): number {
  if (
    typeof waitTimeMinutes === 'number' &&
    Number.isFinite(waitTimeMinutes) &&
    waitTimeMinutes > 0
  ) {
    return waitTimeMinutes * 60 * 1000;
  }
  return DEFAULT_COOLDOWN_MS;
}

export function markActivityLogRateLimited(waitTimeMinutes?: number): void {
  blockedUntil = Date.now() + cooldownMs(waitTimeMinutes);
}

export function isActivityLogPostBlocked(): boolean {
  if (blockedUntil == null) return false;
  if (Date.now() >= blockedUntil) {
    blockedUntil = null;
    return false;
  }
  return true;
}

/** Clears client cooldown (tests only). */
export function clearActivityLogPostBlock(): void {
  blockedUntil = null;
}

type RateLimitBody = {
  code?: string;
  waitTimeMinutes?: number;
};

function getResponseData(error: unknown): RateLimitBody | undefined {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined;
  }
  const response = (error as { response?: { status?: number; data?: unknown } })
    .response;
  if (response?.status !== 429 || response.data == null) {
    return undefined;
  }
  const data = response.data;
  if (typeof data !== 'object' || data === null) {
    return undefined;
  }
  return data as RateLimitBody;
}

export function isActivityLogRateLimitError(error: unknown): boolean {
  const data = getResponseData(error);
  return data?.code === 'ACTIVITY_LOG_RATE_LIMITED';
}

export function parseActivityLogRateLimitResponse(
  error: unknown
): { waitTimeMinutes?: number } | null {
  if (!isActivityLogRateLimitError(error)) {
    return null;
  }
  const data = getResponseData(error);
  return { waitTimeMinutes: data?.waitTimeMinutes };
}

/** Marks cooldown from a 429 response; returns true when the error was a rate limit. */
export function markActivityLogRateLimitedFromError(error: unknown): boolean {
  const parsed = parseActivityLogRateLimitResponse(error);
  if (!parsed) {
    return false;
  }
  markActivityLogRateLimited(parsed.waitTimeMinutes);
  return true;
}
